import React, { useState, useEffect } from 'react';
import { codesApi } from '../services/api';
import { formatCurrency } from '../lib/utils';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  MagnifyingGlass,
  ListChecks,
  Info,
} from '@phosphor-icons/react';

const SERVICE_CATEGORIES = [
  { value: 'PersonalCare', label: 'Personal Care' },
  { value: 'HomeHealth', label: 'Home Health' },
  { value: 'BehavioralHealth', label: 'Behavioral Health' },
  { value: 'Nursing', label: 'Nursing' },
];

const PAYER_TYPES = [
  { value: 'ODM', label: 'ODM' },
  { value: 'DODD', label: 'DODD' },
  { value: 'ManagedCare', label: 'Managed Care' },
];

const CodesPage = () => {
  const [codes, setCodes] = useState([]);
  const [modifiers, setModifiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPayer, setFilterPayer] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [codesRes, modifiersRes] = await Promise.all([
        codesApi.getAll(),
        codesApi.getModifiers(),
      ]);
      setCodes(codesRes.data);
      setModifiers(modifiersRes.data);
    } catch (error) {
      toast.error('Failed to load codes');
    } finally {
      setLoading(false);
    }
  };

  const filteredCodes = codes.filter((code) => {
    const matchesSearch =
      !search ||
      code.code.toLowerCase().includes(search.toLowerCase()) ||
      code.description.toLowerCase().includes(search.toLowerCase());

    const matchesCategory = !filterCategory || code.service_category === filterCategory;
    const matchesPayer = !filterPayer || code.payer_types.includes(filterPayer);

    return matchesSearch && matchesCategory && matchesPayer;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="codes-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Ohio HCPCS Codes
        </h1>
        <p className="text-slate-500 mt-1">
          Pre-loaded Ohio Medicaid billing codes with rates and modifiers
        </p>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                placeholder="Search by code or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="code-search-input"
              />
            </div>
            <Select value={filterCategory || "all"} onValueChange={(val) => setFilterCategory(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter-select">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {SERVICE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPayer || "all"} onValueChange={(val) => setFilterPayer(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full sm:w-40" data-testid="payer-filter-select">
                <SelectValue placeholder="All Payers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payers</SelectItem>
                {PAYER_TYPES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Codes Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredCodes.length === 0 ? (
            <div className="text-center py-12">
              <ListChecks size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No codes match your filters</p>
            </div>
          ) : (
            <TooltipProvider>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-24">Unit</TableHead>
                    <TableHead className="w-24">Rate</TableHead>
                    <TableHead className="w-40">Payers</TableHead>
                    <TableHead>Modifiers</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCodes.map((code) => (
                    <TableRow key={code.code}>
                      <TableCell>
                        <span className="font-mono font-semibold text-indigo-700 text-lg">
                          {code.code}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{code.description}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {code.service_category}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{code.unit_type}</span>
                        <p className="text-xs text-slate-500">{code.unit_minutes} min</p>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono font-semibold text-emerald-600">
                          {formatCurrency(code.rate)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {code.payer_types.map((payer) => (
                            <Badge key={payer} variant="secondary" className="text-xs">
                              {payer}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {code.modifiers.map((mod) => (
                            <Tooltip key={mod}>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-mono cursor-help">
                                  {mod}
                                  <Info size={10} className="text-slate-400" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">{modifiers[mod] || mod}</p>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                          {code.modifiers.length === 0 && (
                            <span className="text-xs text-slate-400">None</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
            Common Modifier Definitions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            {Object.entries(modifiers).slice(0, 9).map(([code, description]) => (
              <div key={code} className="flex gap-2">
                <span className="font-mono font-semibold text-indigo-700 min-w-[32px]">
                  {code}
                </span>
                <span className="text-slate-600">{description}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CodesPage;
